import { createSelector, Selector } from 'reselect'

import {
  IMeta_ProjectConfig_App_Config,
  IMeta_ProjectConfig_App_Config_Nopress,
} from '@kaidam/fc-shared/src/types/schema/project_config'

import {
  INopress_Section_Config,
  INopress_Page,
  INopress_Template_Config,
  INopress_Installation_Config,
  IRecord_Nopress_Page,
  IRecord_Nopress_Pod,
  IRecord_Nopress_Publish,
} from '@kaidam/fc-shared/src/types/schema/nopress'

import {
  IData_Nopress_List_Data,
  INopress_Sidebar_Item,
  INopress_Publish_Config,
} from '@kaidam/fc-shared/src/types/nopress'

import {
  processTemplateConfig,
  podDataHelper,
  pageDataHelper,
  pageAncestorsHelper,
  pageSectionsHelper,
  structureHelper as structureHelperUpstream,
  getFormArray,
  getPodArray,
  getFormSchema,
  getOrgUrl,
} from '@kaidam/fc-shared/src/utils/nopress'

import {
  RootState,
} from '../../types/store'

import rbacSelectors from './rbac'
import projectSelectors from './project'
import routerSelectors from './router'

export const config: Selector<RootState, INopress_Installation_Config> = state => state.nopress.config
export const templateWithData: Selector<RootState, INopress_Template_Config> = state => state.nopress.templateWithData
export const data: Selector<RootState, IData_Nopress_List_Data> = state => state.nopress.data
export const dataLoaded: Selector<RootState, boolean> = state => state.nopress.dataLoaded
export const publishJobs: Selector<RootState, IRecord_Nopress_Publish[]> = state => state.nopress.publishJobs
export const expandedFolders: Selector<RootState, Record<string, boolean>> = state => state.nopress.expandedFolders

export const podData = createSelector(
  data,
  podDataHelper,
)

export const pageData = createSelector(
  data,
  pageDataHelper,
)

export const appConfig = createSelector(
  projectSelectors.appConfig,
  (configs): IMeta_ProjectConfig_App_Config_Nopress => {
    const rawConfig: IMeta_ProjectConfig_App_Config | undefined = configs.find(c => c.namespace == 'nopress')
    if(!rawConfig) {
      return {
        url: {
          mode: 'appconfig',
          field: 'url',
        },
        websites: []
      }
    }
    const appConfig = rawConfig.config as IMeta_ProjectConfig_App_Config_Nopress
    return appConfig
  }
)

export const orgRenderTags = createSelector(
  projectSelectors.currentOrgMap,
  routerSelectors.params,
  (orgMap, {orgid}) => {
    const useOrgNode = orgMap[orgid]
    if(!useOrgNode) return []
    return useOrgNode.meta?.appconfig?.nopress?.renderTags || []
  }
)

export const websiteConfig = createSelector(
  config,
  routerSelectors.params,
  (config, params) => {
    return config.websites.find(w => w.id == params.websiteid)
  }
)

export const publishConfig = createSelector(
  config,
  appConfig,
  websiteConfig,
  routerSelectors.params,
  rbacSelectors.profileMap,
  projectSelectors.currentOrgMap,
  (installationConfig, projectConfig, websiteConfig, {orgid}, profileMap, orgMap): INopress_Publish_Config => {

    const rootProvider = installationConfig.rootProvider
    const orgProfile = profileMap[orgid]
    const orgNode = orgMap[orgid]

    const orgUrl = getOrgUrl({
      projectConfig,
      rootProvider,
      orgProfile,
      orgNode,
    })

    return {
      protocol: process.env.NODE_ENV == 'development' ? 'http' : 'https',
      domain: websiteConfig ? websiteConfig.domain : '',
      path: '/' + orgUrl,
    }
  }
)

export const templateConfigs = createSelector(
  config,
  (config) => config ? config.templates : {}
)

export const websiteTemplate = createSelector(
  templateConfigs,
  websiteConfig,
  (templates, website) => {
    if(!website) return null
    return templates[website.template]
  }
)

export const websiteTemplateConfig = createSelector(
  websiteTemplate,
  (template) => template?.config ? processTemplateConfig(template.config) : null
)

export const websiteTitle = createSelector(
  websiteConfig,
  (websiteConfig) => websiteConfig?.title || 'Website'
)

export const pageAncestors = createSelector(
  templateWithData,
  pageAncestorsHelper,
)

export const pageSections = createSelector(
  templateWithData,
  pageSectionsHelper,
)

export const structureHelper = createSelector(
  templateWithData,
  structureHelperUpstream,
)

export const websiteSidebarMap = createSelector(
  templateWithData,
  websiteTitle,
  (config, websiteTitle) => {
    const map: Record<string, INopress_Sidebar_Item> = {
      root: {
        id: 'root',
        title: websiteTitle,
        type: 'section',
        depth: 0,
        children: config?.content ? config.content.map(s => s.id) : [],
      }
    }

    if(!config) return map

    const collectPage = (page: INopress_Page, depth: number) => {
      const itemPages = page.pages || []
      let type: 'page' | 'folder' | 'collection' = 'page'
      if(page.collection) type = 'collection'
      else if(itemPages.length > 0) type = 'folder'
      const sidebarItem: INopress_Sidebar_Item = {
        page,
        id: page.id,
        title: page.title,
        type: type,
        depth: depth,
        pods: page.pods || [],
        forms: page.forms || [],
        children: itemPages.map(p => p.id), 
      }
      map[page.id] = sidebarItem
      itemPages.forEach(childPage => {
        collectPage(childPage, depth + 1)
      })
      return sidebarItem
    }

    config.content.forEach(section => {
      map[section.id] = {
        id: section.id,
        title: section.title,
        type: 'section',
        depth: 0,
        children: section.pages.map(p => p.id)
      }
      section.pages.forEach(page => {
        collectPage(page, section.hideFolder ? 0 : 1)
      })
    })

    const formWidgets = config.widgets?.forms || {}

    if(Object.keys(formWidgets).length > 0) {

      const formItems: INopress_Sidebar_Item[] = []

      Object
        .keys(formWidgets)
        .forEach(formId => {
          const formWidgetConfig = formWidgets[formId]
          const formConfig = config.forms.find(f => f.id == formWidgetConfig.form)
          const formItem: INopress_Sidebar_Item = {
            id: `widgets_forms_${formId}`,
            title: formConfig.title,
            type: 'form',
            depth: 1,
            icon: 'form',
            children: [],
          }
          formItems.push(formItem)
          map[formItem.id] = formItem
        })

      map.widgets_forms = {
        id: 'widgets_forms',
        title: 'Forms',
        type: 'section',
        depth: 0,
        icon: 'folderSpecial',
        children: formItems.map(i => i.id),
      }      
    }

    return map
  }
)

export const websiteSidebarItems = createSelector(
  templateWithData,
  websiteSidebarMap,
  expandedFolders,
  orgRenderTags,
  (config, map, expandedFolders, orgRenderTags): INopress_Sidebar_Item[] => {
    if(!config) return []
    let list: INopress_Sidebar_Item[] = []

    const collectPage = (page: INopress_Page, depth: number) => {

      if(page.renderTags) {
        const matchingCount = page.renderTags.reduce((all, tag) => {
          return all + orgRenderTags.indexOf(tag) >= 0 ? 1 : 0
        }, 0)
        if(matchingCount < page.renderTags.length) return
      }
      
      list.push(map[page.id])
      const itemPages = page.pages || []
      if(expandedFolders[page.id]) {
        itemPages.forEach(childPage => {
          if(childPage.inherited) return
          collectPage(childPage, depth + 1)
        })
      }
    }

    config.content.forEach(section => {
      if(!section.hideFolder) {
        list.push(map[section.id])
      }
      if(section.hideFolder || expandedFolders[section.id]) {
        section.pages.forEach(page => {
          collectPage(page, section.hideFolder ? 0 : 1)
        })
      }
    })

    const formWidgets = config.widgets?.forms || {}

    if(Object.keys(formWidgets).length > 0) {

      list.push(map.widgets_forms)

      if(expandedFolders['widgets_forms']) {
        map.widgets_forms.children.forEach(id => {
          list.push(map[id])
        })
      }   
    }

    return list
  }
)

export const websiteCurrentItem = createSelector(
  websiteSidebarMap,
  routerSelectors.params,
  (map, params) => {
    let item = map[params.id]
    if(!item) {
      return {
        item: null,
        children: [],
      }
    }

    if(params.collection_id) {
      if(item.children.indexOf(params.collection_id) < 0) {
        return {
          item: null,
          children: [],
        }
      }
      item = map[params.collection_id]
      if(!item) {
        return {
          item: null,
          children: [],
        }
      }
    }
    return {
      item,
      children: item.children.map(id => map[id])
    }
  }
)

export const viewingPods = createSelector(
  templateWithData,
  websiteCurrentItem,
  (config, { item }) => {
    if(!item || !item.pods) return []
    return getPodArray(config, item.pods)
  }
)

export const viewingForms = createSelector(
  templateWithData,
  websiteCurrentItem,
  (config, { item }) => {
    if(!item || !item.forms) return []
    return getFormArray(config, item.forms)
  }
)

export const viewingFormData = createSelector(
  websiteCurrentItem,
  structureHelper,
  viewingForms,
  ({item}, structure, forms) => {
    const schema = forms.reduce((all, form) => {
      return all.concat(form.schema)
    }, [])
    const page = structure.pages[item.id]
    let data = {}
    let id = 'new'
    if(page && page.pageData) {
      data = page.pageData.data
      id = page.pageData.id
    }
    return {
      id,
      data,
      schema,
    }
  }
)

export const viewingPodData = createSelector(
  websiteCurrentItem,
  structureHelper,
  viewingPods,
  ({item}, structure, pods) => {
    const page = structure.pages[item.id]
    if(!page) return []
    return pods.map(config => {
      const data = page.podData && page.podData[config.id] ? page.podData[config.id] : []
      return {
        data,
        config,
      }
    })
  }
)

export const isRootProvider = createSelector(
  projectSelectors.currentOrgMap,
  routerSelectors.params,
  (orgMap, params) => {
    const {
      orgid,
    } = params
    const orgNode = orgMap[orgid]
    if(!orgNode) return false
    return orgNode.meta?.appconfig?.nopress?.rootProvider ? true : false
  }
)

export const pageUrlMap = createSelector(
  structureHelper,
  (structure) => {
    return Object
      .keys(structure.pages)
      .reduce<Record<string, INopress_Page>>((all, id) => {
        const page = structure.pages[id]
        if(page.fullUrl) all[page.fullUrl] = page
        return all
      }, {})
  }
)

export default {
  config,
  data,
  dataLoaded,
  publishJobs,
  podData,
  pageData,
  appConfig,
  expandedFolders,
  websiteConfig,
  publishConfig,
  templateConfigs,
  structure: structureHelper,
  websiteTemplate,
  websiteTemplateConfig,
  websiteSidebarItems,
  websiteSidebarMap,
  pageAncestors,
  pageSections,
  templateWithData,
  websiteCurrentItem,
  viewingPods,
  viewingForms,
  viewingFormData,
  viewingPodData,
  isRootProvider,
  pageUrlMap,
}
